import { Component, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { UserSessionModel } from '../../../core/models/user/current-user.model';
import { UsersService } from '../../../core/services/user/users.service';
import { RolesService } from '../../../core/services/user/roles.service';
import { SpecialismsService } from '../../../core/services/user/specialisms.service';
import { CustomersService } from '../../../core/services/customer/customers.service';
import { CurrentUserService } from '../../../core/services/user/current-user.service';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/services/user/auth.service';
import { GoogleQrCodeDetailsModel } from '../../../core/models/user/google-qr-code-details.model';
import { passwordMatchValidator } from '../../validators/password-match-validator';
import { CookieService } from '../../../core/services/cookie.service';
import { AccountService } from '../../../core/services/account/account.service';
import { SidebarComponent } from '../../../layout/sidebar/sidebar.component';
import { SystemInformationService } from '../../../core/services/systemInformation/system-information.service';
import { SystemInformationModel } from '../../../core/models/systemInformation/system-information.model';
import { UserRole } from '../../../../enums/roles.enum';
import { AuthenticationResponse } from '../../../core/models/authentication-response/authentication-response.model';
import { TwoFactorAuthStatus } from '../../../../enums/two-factor-auth.enum';
import { ToastService } from '../../../core/services/toast.service';
import { InputBlurDirective } from '../../directives/input-blur.directive';
import { LanguagesList } from '../../constants/languages';
import { LanguagesListModel } from '../../../core/models/user/languages-model';
import { InputValidationService } from '../../../core/services/input-validation.service';

declare var bootstrap: any;

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule,
    InputBlurDirective,
  ],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.scss',
})
export class ProfileComponent {
  @ViewChild(SidebarComponent) sidebarComponent!: SidebarComponent;
  generalForm: FormGroup;
  securityForm: FormGroup;
  securityQuestionForm: FormGroup;
  authForm: FormGroup;
  googleLoginForm: FormGroup;
  googleQrCodeForm: FormGroup;
  mailCodeForm: FormGroup;
  mailLoginForm: FormGroup;
  mailNotificationForm: FormGroup;
  generalVisible: boolean = true;
  activeTab: string = 'general';
  title: string = '';
  securityVisible: boolean = false;
  showPasswordArea: 'oldPassword' | 'newPassword' | 'confirmPassword';
  oldPasswordVisible: boolean = false;
  newPasswordVisible: boolean = false;
  confirmPasswordVisible: boolean = false;
  roles: any = [];
  userId: string = '';
  specialismList: any = [];
  organizationList: any = [];
  maxDate: string;
  fullName: string = '';
  currentUser: UserSessionModel | null = null;
  isOverlayLoading: boolean = false;
  file: File | null = null;
  fileCount: any = null;
  fileSelector = document.createElement('input');
  tempProfilePhoto = '';
  isProfilePhotoUpdated: boolean = false;
  isDeleteClicked: boolean = false;
  showMailSection: boolean = false;
  mailAuthSectionLoading: boolean = false;
  previewImage: string | ArrayBuffer | null = null;
  contentHeader = {
    headerTitle: this.translate.instant('navbar.profile'),
    actionButton: false,
  };
  googleQrCodeDetails: GoogleQrCodeDetailsModel = {
    qrCodeImageUrl: '',
    manuelEntrySetupCode: '',
  };
  googleAuthToggle = false;
  isGoogleActivated: boolean = true;
  isGoogleChecked: boolean = false;
  isMailChecked: boolean = false;
  currentPath: string | null = null;
  userRole: any = UserRole;
  isEmailNotificationsEnabled: boolean = false;
  languages: LanguagesListModel[] = [];
  securityQuestions = [
    'questions.favouriteRestaurant',
    'questions.firstPet',
    'questions.historicalPerson',
    'questions.firstBoss',
    'questions.favouriteAuthor',
  ];

  constructor(
    private router: Router,
    private usersService: UsersService,
    private accountService: AccountService,
    private rolesService: RolesService,
    private specialismsService: SpecialismsService,
    private customersService: CustomersService,
    private currentUserService: CurrentUserService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private readonly systemInformationService: SystemInformationService,
    private toastService: ToastService,
    private inputValidationService: InputValidationService
  ) {
    const userLang = localStorage.getItem('selectedLanguage') || 'en';
    this.initializeLanguages();

    this.translate.use(userLang).subscribe(() => {
      this.translate.get('profile.general').subscribe((translation: string) => {
        this.title = translation;
      });
    });

    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];
    this.generalForm = this.fb.group({
      id: [''],
      name: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      surname: [
        '',
        [
          Validators.required,
          Validators.pattern(/^(?!.*[ğĞçÇşŞıİ])([a-zA-ZÀ-ÿĲĳ\s]*)$/u),
        ],
      ],
      phoneNumber: [
        '',
        [Validators.required, Validators.pattern('^\\+?[0-9]{7,15}$')],
      ],
      email: ['', [Validators.required, Validators.email]],
      selectedLanguageCode: ['', [Validators.required]],
      mailNotification: [null],
    });

    this.securityForm = this.fb.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/
            ),
          ],
        ],
        confirmPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/
            ),
          ],
        ],
        oldPassword: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            Validators.pattern(
              /^^(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/
            ),
          ],
        ],
      },
      { validators: passwordMatchValidator }
    );

    this.securityQuestionForm = this.fb.group({
      currentPassword: ['', [Validators.required]],
      question: [null, [Validators.required]],
      answer: ['', [Validators.required]],
    });

    this.authForm = this.fb.group({
      googleAuth: [{ value: false, disabled: false }],
      mailAuth: [{ value: false, disabled: false }],
    });
    this.googleQrCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.googleLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailCodeForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailLoginForm = this.fb.group({
      code: ['', [Validators.required]],
    });
    this.mailNotificationForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.currentPath = this.route.snapshot.routeConfig?.path;
    this.currentUser = this.currentUserService.GetSession();
    this.getSystemInformation();
    this.getUserById();
    this.getSpecialisms();
    this.getRoles();
    this.getOrganizations();

    const modalElement = document.getElementById('createGoogleQrCodeModal');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', () => {
        if (!this.isGoogleActivated) {
          this.onModalClose();
        } else {
          const controlNames = ['mailAuth', 'googleAuth'];
          const shouldEnableAll =
            this.currentUser.authenticatorType === TwoFactorAuthStatus.None;

          controlNames.forEach((controlName) => {
            const control = this.authForm.get(controlName);
            if (shouldEnableAll) {
              control?.patchValue(false);
              control?.enable();
            } else {
              control?.value ? control?.enable() : control?.disable();
            }
          });
        }
      });
    }
  }

  systemInformation!: SystemInformationModel;
  getSystemInformation() {
    this.systemInformationService.getSystemInformation().subscribe({
      next: (res) => {
        if (res[0] && !res[0].id) return;
        this.systemInformation = res[0];
      },
    });
  }

  private initializeLanguages() {
    this.languages = LanguagesList.map((lang) => ({
      ...lang,
      name: this.translate.instant(lang.name),
    }));
  }

  onModalClose(): void {
    this.updateAuthFormValues();
  }

  getUserById() {
    this.userId = this.currentUser.id;
    if (this.userId) {
      this.usersService.getUser(this.userId).subscribe({
        next: (res) => {
          this.generalForm.patchValue({
            id: res.id,
            name: res.name,
            surname: res.surname,
            email: res.email,
            phoneNumber: res.phoneNumber,
            selectedLanguageCode: res.selectedLanguageCode,
          });
          this.mailNotificationForm.patchValue({
            email: res.generalPractitionerNotificationEmail ?? res.email,
          });
          this.fullName = `${res.name} ${res.surname}`;
          this.isEmailNotificationsEnabled = res.isEmailNotificationsEnabled;

          if (res.authenticatorType === 'Google') {
            this.authForm.patchValue({ googleAuth: true, mailAuth: false });
            this.authForm.get('mailAuth')?.disable();
            this.authForm.get('googleAuth')?.enable();
          } else if (res.authenticatorType === 'Email') {
            this.authForm.patchValue({ googleAuth: false, mailAuth: true });
            this.authForm.get('googleAuth')?.disable();
            this.authForm.get('mailAuth')?.enable();
          } else {
            this.authForm.patchValue({ googleAuth: false, mailAuth: false });
            this.authForm.get('googleAuth')?.enable();
            this.authForm.get('mailAuth')?.enable();
          }

          this.previewImage = res.folderPath
            ? `data:image/jpeg;base64,${res.folderPath}`
            : '';
        },
      });
    }
  }

  getSpecialisms() {
    this.specialismsService.getSpecialisms().subscribe({
      next: (res) => {
        this.specialismList = res;
      },
    });
  }

  getRoles() {
    this.rolesService.getRoles().subscribe({
      next: (res) => {
        this.roles = res.items;
      },
    });
  }

  getOrganizations() {
    this.customersService.allCustomerCodes().subscribe({
      next: (res) => {
        this.organizationList = res;
      },
    });
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toISOString();
  }

  get isFileSizeValid() {
    if (this.fileSelector.files && this.fileSelector.files.length > 0) {
      return !(this.fileSelector.files[0].size > 1000000);
    }
    return true;
  }

  save(form: 'securityForm' | 'generalForm') {
    switch (form) {
      case 'securityForm':
        if (
          this.f['password'].value &&
          this.f['oldPassword'].value &&
          this.f['password'].value == this.f['oldPassword'].value
        ) {
          Swal.fire({
            title: this.translate.instant('warning'),
            text: this.translate.instant('alerts.newOldPasswordCheck'),
            icon: 'warning',
            showCancelButton: false,
            confirmButtonColor: '#7367F0',
            cancelButtonColor: '#E42728',
            confirmButtonText: this.translate.instant('yes'),
          }).then((result) => {
            if (result.value) {
              Swal.close();
            }
          });
          return;
        } else {
          const model = {
            newPassword:
              this.f['password'].value != null || undefined || ''
                ? this.f['password'].value
                : null,
            oldPassword: this.f['oldPassword'].value,
          };
          this.saveHandler(model, 'securityForm');
        }
        break;
      case 'generalForm':
        // if (this.generalForm.invalid) {
        //   return;
        const model = {
          id: this.generalForm.get('id')?.value,
          name: this.generalForm.get('name')?.value || null,
          surname: this.generalForm.get('surname')?.value || null,
          email: this.generalForm.get('email')?.value || null,
          phoneNumber: this.generalForm.get('phoneNumber')?.value || null,
          languageCode:
            this.generalForm.get('selectedLanguageCode')?.value || null,
        };
        this.saveHandler(model, 'generalForm');
        break;
    }
  }

  updateSecurityQuestion(): void {
    if (this.securityQuestionForm.invalid) {
      this.securityQuestionForm.markAllAsTouched();
      return;
    }

    this.accountService
      .updateSecurityQuestion(this.securityQuestionForm.getRawValue())
      .subscribe({
        next: () => {
          this.securityQuestionForm.reset();
          this.toastService.showToast(
            this.translate.instant('informations.accountUpdated'),
            'success'
          );
        },
      });
  }
  setLanguage(lang: string) {
    this.translate.use(lang);
    localStorage.setItem('selectedLanguage', lang);
  }

  saveHandler(model, form: 'securityForm' | 'generalForm') {
    if (!this.isFileSizeValid) {
      Swal.fire({
        title: this.translate.instant('areYouSure'),
        text: this.translate.instant('wontBeAbleToRevert'),
        icon: 'warning',
        showCancelButton: false,
        confirmButtonColor: '#7367F0',
        cancelButtonColor: '#E42728',
        confirmButtonText: this.translate.instant('yes'),
      }).then((result) => {
        if (result.value) {
          Swal.close();
        }
      });
      return;
    }

    const service =
      form == 'securityForm'
        ? this.accountService.putPassword(model)
        : this.accountService.editAccount(model);

    this.currentUser = this.currentUserService.GetSession();
    this.currentUser.selectedLanguageCode = this.generalForm.get(
      'selectedLanguageCode'
    )?.value;
    this.currentUserService.SetSession(this.currentUser);

    service.subscribe({
      next: (res) => {
        if (form === 'securityForm') {
          this[form].reset();
        }
        this.updateCurrentUserSession(res);
        this.routeAfterChange();
        this.setLanguage(this.generalForm.get('selectedLanguageCode')?.value);

        const message =
          form === 'securityForm'
            ? 'informations.newPasswordSetted'
            : 'informations.accountUpdated';
        this.toastService.showToast(this.translate.instant(message), 'success');

        // this.updateCurrentUserSession(res);
        this.setLanguage(this.generalForm.get('selectedLanguageCode')?.value);
        this.routeAfterChange();
      },
    });
  }

  routeAfterChange() {
    switch (this.currentUser.role) {
      case this.userRole.GeneralPractitioner:
        this.router.navigate(['/patient/list']);
        break;
      case this.userRole.Dermatologist:
        this.router.navigate(['/consultation/list']);
        break;
      default:
        this.router.navigate(['/dashboard/main']);
    }
  }

  showPassword(showPasswordArea: string): void {
    switch (showPasswordArea) {
      case 'oldPassword':
        this.oldPasswordVisible = !this.oldPasswordVisible;
        const oldPasswordInput = document.getElementById(
          'inputOldPassword'
        ) as HTMLInputElement;
        if (oldPasswordInput) {
          oldPasswordInput.type = this.oldPasswordVisible ? 'text' : 'password';
        }
        break;
      case 'newPassword':
        this.newPasswordVisible = !this.newPasswordVisible;
        const newPasswordInput = document.getElementById(
          'inputNewPassword'
        ) as HTMLInputElement;
        if (newPasswordInput) {
          newPasswordInput.type = this.newPasswordVisible ? 'text' : 'password';
        }
        break;
      case 'confirmPassword':
        this.confirmPasswordVisible = !this.confirmPasswordVisible;
        const confirmPasswordInput = document.getElementById(
          'inputNewConfirmPassword'
        ) as HTMLInputElement;
        if (confirmPasswordInput) {
          confirmPasswordInput.type = this.confirmPasswordVisible
            ? 'text'
            : 'password';
        }
        break;
    }
  }

  updateCurrentUserSession(res: any) {
    let currentUser = this.currentUserService.GetSession();
    //currentUser.avatarBase64 = res.avatarBase64;
    currentUser.name = res.name;
    currentUser.username = res.username;
    currentUser.lastName = res.surname;
    currentUser.phoneNumber = res.phoneNumber;
    currentUser.email = res.email;
    currentUser.selectedLanguageCode = res.languageCode;

    if (res && res?.token?.token) {
      currentUser.token = res.token.token;
    }

    this.currentUserService.SetSession(currentUser);
  }

  deleteProfilePhoto = () => {
    Swal.fire({
      title: this.translate.instant('areYouSure'),
      text: this.translate.instant('wontBeAbleToRevert'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#7367F0',
      cancelButtonColor: '#E42728',
      confirmButtonText: this.translate.instant('yes'),
    }).then((result) => {
      if (result.value) {
        this.accountService.deleteProfilePhoto(this.userId).subscribe({
          next: () => {
            this.tempProfilePhoto =
              '../../../../assets/images/avatars/user.png';
            this.isDeleteClicked = true;
            this.isProfilePhotoUpdated = false;
            //this.clearFileSelector();
            this.previewImage = null;
          },
        });

        // this.accountService.deleteProfilePhoto(this.userId);
      }
    });
  };

  isControlInvalid(controlName: string): boolean {
    const control = this.mailNotificationForm.get(controlName);
    return control?.invalid && (control?.touched || control?.dirty);
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
  }

  handleFileUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.fileCount = input.files.length;
      this.file = input.files[0];
      input.value = '';
    }
  }
  isResetPasswordControlInvalid(controlName: string): boolean {
    const control = this.securityForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onGoogleAuthToggle(e: Event): void {
    this.isGoogleChecked = (e.target as HTMLInputElement).checked;

    if (this.isGoogleChecked) {
      this.authForm.get('mailAuth')?.disable();
    } else {
      this.authForm.get('mailAuth')?.enable();
    }

    this.accountService.getGoogleAuth().subscribe({
      next: async (res: GoogleQrCodeDetailsModel) => {
        this.googleQrCodeDetails.qrCodeImageUrl = res?.qrCodeImageUrl;
        this.googleQrCodeDetails.manuelEntrySetupCode =
          res?.manuelEntrySetupCode;

        const modal = new bootstrap.Modal(
          document.getElementById('createGoogleQrCodeModal')
        );
        modal.show();
      },
    });
  }

  submitGoogleQrCodeForm(e: Event): void {
    e.preventDefault();

    setTimeout(() => {
      if (!this.googleQrCodeForm.valid) {
        this.handleInvalidForm();
        this.updateAuthFormValues();
        return;
      }

      const formData = { ...this.googleQrCodeForm.value };
      if (
        this.isGoogleChecked &&
        (this.currentUser.authenticatorType === TwoFactorAuthStatus.None ||
          this.currentUser.authenticatorType !== TwoFactorAuthStatus.Google)
      ) {
        this.handleEnableGoogleAuth(formData);
      } else {
        this.handleDisableGoogleAuth(formData);
      }
    }, 500);
  }

  private handleEnableGoogleAuth(formData: any): void {
    this.accountService.enableGoogleAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleAuthResponse(res);
      },
    });
  }

  private handleDisableGoogleAuth(formData: any): void {
    this.accountService.disableGoogleAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleAuthResponse(res);
      },
    });
    this.googleQrCodeForm.markAllAsTouched();
  }

  private handleAuthResponse(res: AuthenticationResponse): void {
    if (res.result) {
      this.onModalClose();

      this.toastService.showToast(
        this.translate.instant('successProcessDesc'),
        'success'
      );

      this.updateAuthFormValues();
      this.signOut();
    } else {
      this.isGoogleActivated = false;
      this.onModalClose();
      this.updateAuthFormValues();

      this.toastService.showToast(
        this.translate.instant('codeNotCorrect'),
        'error'
      );
    }
  }

  private handleInvalidForm(): void {
    this.toastService.showToast(
      this.translate.instant('warningRequiredCodeDesc'),
      'warning'
    );
    this.googleQrCodeForm.markAllAsTouched();
  }

  signOut() {
    this.authService.logout().subscribe(() => {
      this.cookieService.deleteCookie('applications');
    });
  }
  isGoogleLoginControlInvalid(controlName: string): boolean {
    const control = this.googleLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }
  isFirstLoginControlInvalid(controlName: string): boolean {
    const control = this.mailLoginForm.get(controlName);
    return control ? control.invalid && control.touched : false;
  }

  onMailAuthToggle(e: Event): void {
    this.isMailChecked = (e.target as HTMLInputElement).checked;
    this.authForm.get('googleAuth')?.disable();
    this.showMailSection = true;
    if (this.isMailChecked) {
      this.accountService.sendEmailAuthForEnable().subscribe({
        next: async () => {
          this.showMailSection = true;
        },
      });
    } else {
      this.accountService.sendEmailAuthForDisable().subscribe({
        next: async () => {
          this.showMailSection = true;
        },
      });
    }
  }

  submitMailAuthCode(e: Event): void {
    e.preventDefault();

    if (!this.mailCodeForm.valid) {
      this.handleInvalidMailForm();
      return;
    }

    const formData = { ...this.mailCodeForm.value };

    if (
      this.isMailChecked &&
      (this.currentUser.authenticatorType === TwoFactorAuthStatus.None ||
        this.currentUser.authenticatorType !== TwoFactorAuthStatus.Email)
    ) {
      this.handleEnableMailAuth(formData);
    } else {
      this.handleDisableMailAuth(formData);
    }
  }

  private handleEnableMailAuth(formData: any): void {
    this.mailAuthSectionLoading = true;

    this.accountService.enableEmailAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        this.handleMailAuthResponse(res);
      },
    });
  }

  private handleDisableMailAuth(formData: any): void {
    this.mailAuthSectionLoading = true;
    this.accountService.disableEmailAuth(formData).subscribe({
      next: (res: AuthenticationResponse) => {
        if (res.result) {
          this.authForm.patchValue({ googleAuth: false, mailAuth: false });
          this.authForm.get('mailAuth')?.enable();
          this.authForm.get('googleAuth')?.enable();
        }
        this.handleMailAuthResponse(res);
      },
    });

    this.mailCodeForm.markAllAsTouched();
  }

  private handleMailAuthResponse(res: AuthenticationResponse): void {
    setTimeout(() => {
      if (res.result) {
        this.mailAuthSectionLoading = false;
        this.showMailSection = false;
        this.authForm.get('googleAuth')?.enable();
        this.toastService.showToast(
          this.translate.instant('successProcessDesc'),
          'success'
        );
        this.signOut();
      } else {
        this.mailAuthSectionLoading = false;
        this.showMailSection = false;
        this.updateAuthFormValues();
        this.toastService.showToast(
          this.translate.instant('codeNotCorrect'),
          'error'
        );
      }
    }, 2000);
  }

  private handleInvalidMailForm(): void {
    this.toastService.showToast(
      this.translate.instant('warningRequiredCodeDesc'),
      'warning'
    );
    this.mailCodeForm.markAllAsTouched();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];

      const reader = new FileReader();
      reader.onload = (e) => (this.previewImage = e.target?.result);
      reader.readAsDataURL(file);

      const formData = new FormData();
      formData.append('Image', file);

      this.accountService.uploadProfilePhoto(formData).subscribe({
        next: async () => {
          this.toastService.showToast(
            this.translate.instant('informations.profilePhotoUpdated'),
            'success'
          );

          this.usersService.userAvatar = null;
          await this.usersService.getUserAvatar();
          this.sidebarComponent?.getUserAvatar();
        },
      });
    }
  }
  updateAuthFormValues() {
    this.authForm.patchValue({
      googleAuth:
        this.currentUser.authenticatorType === TwoFactorAuthStatus.Google,
      mailAuth:
        this.currentUser.authenticatorType === TwoFactorAuthStatus.Email,
    });

    const controlNames = ['mailAuth', 'googleAuth'];
    const shouldEnableAll =
      this.currentUser.authenticatorType === TwoFactorAuthStatus.None;

    controlNames.forEach((controlName) => {
      const control = this.authForm.get(controlName);
      if (shouldEnableAll) {
        control?.patchValue(false);
        control?.enable();
      } else {
        control?.value ? control?.enable() : control?.disable();
      }
    });
  }

  setTitle(title: string) {
    switch (title) {
      case 'general':
        this.title = this.translate.instant('profile.general');
        break;
      case 'security':
        this.title = this.translate.instant('profile.security');
        break;
      case 'authentication':
        this.title = this.translate.instant('profile.authentication');
        break;
    }
  }

  get f() {
    return this.securityForm.controls;
  }
  get g() {
    return this.generalForm.controls;
  }

  onMailNotificationChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    if (selectElement.value === 'true') {
      const modal = new bootstrap.Modal(
        document.getElementById('mailNotificationModal')
      );
      modal.show();
    } else {
      this.sendMailNotification(false);
    }
  }

  preventForbiddenCharacters(
    event: Event,
    type: 'number' | 'text' | 'email' | 'tel'
  ): void {
    this.inputValidationService.preventForbiddenCharacters(event, type);
  }

  onMailNotificationSubmit() {
    if (this.mailNotificationForm.invalid) {
      this.mailNotificationForm.markAllAsTouched();
      return;
    }

    this.sendMailNotification(true);
  }

  sendMailNotification(isEnabled: boolean) {
    const userData = {
      userId: this.userId,
      email: this.mailNotificationForm.value.email,
      isEmailNotificationsEnabled: isEnabled,
    };

    this.usersService.sendUserNotificationSettings(userData).subscribe({
      next: () => {
        this.toastService.showToast(
          this.translate.instant('successSaveAndTakePictureDesc'),
          'success'
        );
        const modal = bootstrap.Modal.getInstance(
          document.getElementById('mailNotificationModal')
        );
        modal.hide();
      },
    });
  }
}
